// Composant Card Synthese par Metier
// Affiche radar, scores, top/flop, et progression par theme

import { Card } from '../ui'
import RadarChart, { type ScoresAxes, type MaxParAxe } from './RadarChart'

// Configuration des 5 axes HALTES
const HALTES_AXES_CONFIG = [
  { key: 'H', label: 'Hygiène' },
  { key: 'A', label: 'Accès' },
  { key: 'LT', label: 'Livraisons / Tâches' },
  { key: 'E', label: 'Énergies' },
  { key: 'S', label: 'Secours' },
] as const

export interface ThemeScore {
  themeId: string
  themeName: string
  axeHaltes: string
  score: number
  max: number
  percent: number
}

export interface MetierSynthesisCardProps {
  metier: {
    code: string
    nom: string
    ordre: number
  }
  scores: ScoresAxes
  maxScores: MaxParAxe
  totalScore: number
  maxTotal: number
  themeScores: ThemeScore[]
  showRadar?: boolean
}

export default function MetierSynthesisCard({
  metier,
  scores,
  maxScores,
  totalScore,
  maxTotal,
  themeScores,
  showRadar = true,
}: MetierSynthesisCardProps) {
  const percent = maxTotal > 0 ? Math.round((totalScore / maxTotal) * 100) : 0

  // Trier les themes pour top 3 et flop 3
  const sortedThemes = [...themeScores].sort((a, b) => b.percent - a.percent)
  const top3 = sortedThemes.slice(0, 3)
  const flop3 = sortedThemes.slice(-3).reverse()

  // Determiner la couleur du score
  const getScoreColor = () => {
    if (percent >= 70) return { text: 'text-green-600', bg: 'bg-green-500' }
    if (percent >= 40) return { text: 'text-orange-600', bg: 'bg-orange-500' }
    return { text: 'text-red-600', bg: 'bg-red-500' }
  }

  const scoreColor = getScoreColor()

  // Couleur de la barre de progression par theme
  const getProgressBarColor = (pct: number) => {
    if (pct >= 70) return 'bg-green-500'
    if (pct >= 40) return 'bg-orange-500'
    return 'bg-red-500'
  }

  return (
    <Card className="overflow-hidden print-avoid-break py-0">
      {/* Header style wizard comme card casques */}
      <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <span className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold">
            {metier.nom}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-lg text-sm font-bold text-white ${scoreColor.bg}`}>
            {percent}%
          </span>
          <span className="text-sm text-gray-500">
            ({totalScore}/{maxTotal})
          </span>
        </div>
      </div>

      <div className="p-5">
        {/* Zone principale : Radar + Top/Flop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Radar */}
          {showRadar && (
            <div className="flex justify-center">
              <RadarChart scores={scores} maxScores={maxScores} height={180} showLabels={false} />
            </div>
          )}

          {/* Top 3 / Flop 3 */}
          <div className="space-y-3">
            {/* Top 3 */}
            <div>
              <h4 className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                Points forts
              </h4>
              <div className="space-y-1">
                {top3.map((theme, i) => (
                  <div key={theme.themeId} className="flex items-center gap-2 text-xs">
                    <span className="w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <span className="flex-1 truncate capitalize">{theme.themeName}</span>
                    <span className="font-medium text-green-700">{theme.percent}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Flop 3 */}
            <div>
              <h4 className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                A ameliorer
              </h4>
              <div className="space-y-1">
                {flop3.map((theme, i) => (
                  <div key={theme.themeId} className="flex items-center gap-2 text-xs">
                    <span className="w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <span className="flex-1 truncate capitalize">{theme.themeName}</span>
                    <span className="font-medium text-red-700">{theme.percent}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Liste des 5 axes HALTES avec progression */}
        <div className="pt-3 border-t border-gray-100 space-y-2">
          <h4 className="text-xs font-semibold text-gray-700 mb-2">
            Progression par axe
          </h4>
          {HALTES_AXES_CONFIG.map((axeConfig) => {
            const axeScore = scores[axeConfig.key as keyof typeof scores] || 0
            const axeMax = maxScores[axeConfig.key as keyof typeof maxScores] || 0
            const axePercent = axeMax > 0 ? Math.round((axeScore / axeMax) * 100) : 0
            const isNotApplicable = axeMax === 0

            return (
              <div key={axeConfig.key} className={`group ${isNotApplicable ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-2 text-xs mb-1">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${isNotApplicable ? 'bg-gray-200 text-gray-400' : 'bg-gray-100 text-gray-700'}`}>
                    {axeConfig.key}
                  </span>
                  <span className={`flex-1 ${isNotApplicable ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                    {axeConfig.label}
                    {isNotApplicable && ' (N/A)'}
                  </span>
                  <span className={`font-medium ${isNotApplicable ? 'text-gray-400' : 'text-gray-900'}`}>
                    {isNotApplicable ? '—' : `${axePercent}%`}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  {!isNotApplicable && (
                    <div
                      className={`h-full ${getProgressBarColor(axePercent)} transition-all`}
                      style={{ width: `${axePercent}%` }}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Card>
  )
}

// Version compacte pour print
export function MetierSynthesisCardPrint({
  metier,
  scores,
  maxScores,
  totalScore,
  maxTotal,
  themeScores,
}: Omit<MetierSynthesisCardProps, 'showRadar'>) {
  const percent = maxTotal > 0 ? Math.round((totalScore / maxTotal) * 100) : 0
  const sortedThemes = [...themeScores].sort((a, b) => b.percent - a.percent)

  return (
    <div className="print-avoid-break border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-900">
          {metier.nom}
        </h3>
        <span className="text-lg font-bold">
          {percent}% ({totalScore}/{maxTotal})
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Radar mini */}
        <RadarChart scores={scores} maxScores={maxScores} height={150} showLabels={false} />

        {/* Top/Flop compact */}
        <div className="text-xs space-y-2">
          <div>
            <strong className="text-green-700">Points forts :</strong>
            <ul className="mt-1">
              {sortedThemes.slice(0, 3).map((t) => (
                <li key={t.themeId} className="capitalize">
                  {t.themeName} ({t.percent}%)
                </li>
              ))}
            </ul>
          </div>
          <div>
            <strong className="text-red-700">A ameliorer :</strong>
            <ul className="mt-1">
              {sortedThemes.slice(-3).map((t) => (
                <li key={t.themeId} className="capitalize">
                  {t.themeName} ({t.percent}%)
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
